export type SubscriptionSource = "square" | "apple" | "google" | "manual";
export type SubscriptionTier = "startingGate" | "bronze" | "silver" | "gold";
export type SubscriptionFeature = "lapTimes" | "liveStream" | "videoClips";
export type SubscriptionStatus =
	| "active"
	| "expired"
	| "canceled"
	| "billingRetry"
	| "gracePeriod"
	| "revoked";

export interface UserEntitlementRecord {
	uid?: string;
	source: SubscriptionSource;
	tier: SubscriptionTier;
	features: SubscriptionFeature[];
	status: SubscriptionStatus;
	productId?: string;
	providerSubscriptionId?: string;
	originalTransactionId?: string;
	latestTransactionId?: string;
	expiresAt?: any;
	autoRenewStatus?: boolean | string;
	environment?: "sandbox" | "production";
	lastVerifiedAt?: any;
	createdAt?: any;
	updatedAt?: any;
	reason?: string;
	grantedBy?: string;
	permanent?: boolean;
}

export interface UserEntitlementSummary {
	currentTier: SubscriptionTier;
	currentEntitlements: SubscriptionFeature[];
	subscriptionSources: SubscriptionSource[];
	subscriptionStatus: SubscriptionStatus | "";
	subscriptionExpiresAt?: any;
}

export const SUBSCRIPTION_TIER_RANK: Record<SubscriptionTier, number> = {
	startingGate: 0,
	bronze: 1,
	silver: 2,
	gold: 3,
};

export const SUBSCRIPTION_TIER_FEATURES: Record<
	SubscriptionTier,
	SubscriptionFeature[]
> = {
	startingGate: [],
	bronze: ["lapTimes"],
	silver: ["lapTimes", "liveStream"],
	gold: ["lapTimes", "liveStream", "videoClips"],
};

export const SUBSCRIPTION_TIER_PRODUCT_IDS: Record<
	Exclude<SubscriptionTier, "startingGate">,
	string
> = {
	bronze: "flaggerai.bronze.monthly",
	silver: "flaggerai.silver.monthly",
	gold: "flaggerai.gold.monthly",
};

export function getTierForFeature(feature: SubscriptionFeature): SubscriptionTier {
	switch (feature) {
		case "lapTimes":
			return "bronze";
		case "liveStream":
			return "silver";
		case "videoClips":
			return "gold";
	}
}

export function hasTierAtLeast(
	currentTier: SubscriptionTier | "",
	requiredTier: SubscriptionTier
): boolean {
	if (!currentTier) return false;
	return (
		SUBSCRIPTION_TIER_RANK[currentTier] >= SUBSCRIPTION_TIER_RANK[requiredTier]
	);
}

export function featuresForTier(tier: SubscriptionTier): SubscriptionFeature[] {
	return [...SUBSCRIPTION_TIER_FEATURES[tier]];
}

export function normalizeTier(value: string): SubscriptionTier {
	const clean = `${value || ""}`.trim().toLowerCase();
	if (clean === "gold") return "gold";
	if (clean === "silver") return "silver";
	if (clean === "bronze") return "bronze";
	return "startingGate";
}

export function tierFromFeatures(features: string[] = []): SubscriptionTier {
	const featureSet = new Set(features);
	if (featureSet.has("videoClips")) return "gold";
	if (featureSet.has("liveStream")) return "silver";
	if (featureSet.has("lapTimes")) return "bronze";
	return "startingGate";
}

export type SubscriptionPlanAction = "Current Plan" | "Upgrade" | "Downgrade";

export function getSubscriptionPlanAction(
	currentTier: SubscriptionTier,
	planTier: SubscriptionTier,
	isCurrentPlan: boolean
): SubscriptionPlanAction {
	if (isCurrentPlan) return "Current Plan";
	return SUBSCRIPTION_TIER_RANK[planTier] > SUBSCRIPTION_TIER_RANK[currentTier]
		? "Upgrade"
		: "Downgrade";
}
